/**
 * "Inverter direção de leitura" — mirrors an ENTIRE manga page horizontally:
 * panel geometry (polygon), panel art (`flipX`, plus the pan `offsetX` so a
 * framed/panned shot mirrors correctly too — see the derivation below), and
 * every bubble's POSITION/tail/rotation — but never the bubble's TEXT
 * CONTENT, which must stay legible (not mirrored).
 *
 * Reversible: applying this function twice returns the page to its original
 * geometry (up to floating-point epsilon and the usual `undefined`↔`false`
 * normalization boolean toggles go through — see the round-trip test in
 * scratchpad/manga-mirror-roundtrip.mjs referenced in the implementation PR).
 */
import type { MangaPage, MangaPanel, MangaBubble, MangaLayout } from './api';

/** Fallback page pixel size — same defaults `manga-editor.tsx` itself uses
 * when a page's own `width`/`height` are missing/zero. Only affects the
 * dx/dy-dominance test below (a purely cosmetic tail-bow decision), never
 * the reversibility of the transform itself. */
const FALLBACK_PAGE_W = 1000;
const FALLBACK_PAGE_H = 1414;

/**
 * Mirrors a single panel's geometry + art framing horizontally:
 *  - `points`: each corner's x → 1 - x (same winding/point order — a simple
 *    convex/concave polygon's CSS `clip-path`/SVG fill covers the identical
 *    region regardless of winding direction, and nothing else in the
 *    codebase reads point order for winding, so no reversal is needed).
 *  - `flipX`: toggled — mirrors the art pixels themselves within the frame.
 *  - `offsetX`: negated — the pan offset must flip too, or a framed/panned
 *    shot would show the WRONG slice of the source image once the frame
 *    itself sits at a mirrored screen position (see file header + PR notes:
 *    derived from the actual `translate(offsetX%) scale(flipX?-1:1)` CSS
 *    chain every renderer uses — offsetX unchanged would NOT reproduce the
 *    true mirror, only offsetX negated does, verified numerically).
 *  - `offsetY`, `flipY`, `scale`, `border`, images, `script`: untouched — a
 *    HORIZONTAL mirror never touches the vertical axis or the art assets.
 */
function mirrorPanel(p: MangaPanel): MangaPanel {
  return {
    ...p,
    points: p.points.map((pt) => ({ x: 1 - pt.x, y: pt.y })),
    flipX: !p.flipX,
    offsetX: p.offsetX !== undefined ? -p.offsetX : p.offsetX,
  };
}

/**
 * Mirrors a single bubble's POSITION and geometry — never its `text`,
 * `fontFamily`, or any other content field, so dialogue stays legible.
 *
 *  - `x`: `1 - x - w` (top-left corner reflected, box width unchanged).
 *  - `tail.x`: negated (`tail.y` is untouched — vertical component of the
 *    tail doesn't change under a horizontal mirror).
 *  - `tailFlip`: the curved tail's bow (and, identically, the thought
 *    bubble's trailing circles — both in bubble-path.ts share the exact
 *    same `sideFlip` formula) pick their side from whichever axis dominates
 *    the tail direction: `|dx| > |dy| * 0.3` ⇒ "dx-dominant" branch, else
 *    "dy-dominant". Verified numerically (dense angle sweep, see PR notes):
 *      · dx-dominant tails auto-correct — the sign flip already baked into
 *        `tail.x` negating is exactly what the bow needs, so `tailFlip`
 *        passes through UNCHANGED.
 *      · dy-dominant (near-vertical) tails do NOT auto-correct — the
 *        `sideFlip` formula's dy-branch doesn't reference `dx` at all, so
 *        the manual `tailFlip` must be INVERTED to compensate.
 *    The dominance test itself is mirror-INVARIANT (only `dx`'s sign flips,
 *    not its magnitude), so this is stable across repeated mirrors —
 *    applying the transform twice restores the original `tailFlip`.
 *  - `rotation`: negated — a reflection conjugates any rotation to its
 *    inverse (`-θ`), regardless of the mirror axis's position (a standard
 *    dihedral-group fact, not specific to where the bubble sits on the
 *    page) — so the WHOLE bubble (body + tail + text) keeps rotating
 *    exactly as before, just mirrored.
 *  - Everything else (`w`, `h`, `type`, `text`, style fields…): untouched.
 */
function mirrorBubble(b: MangaBubble, pageW: number, pageH: number): MangaBubble {
  const next: MangaBubble = { ...b, x: 1 - b.x - b.w };
  if (b.tail) {
    const dxPx = b.tail.x * pageW;
    const dyPx = b.tail.y * pageH;
    const dxDominant = Math.abs(dxPx) > Math.abs(dyPx) * 0.3;
    next.tail = { x: -b.tail.x, y: b.tail.y };
    next.tailFlip = dxDominant ? b.tailFlip : !b.tailFlip;
  }
  if (b.rotation !== undefined) next.rotation = -b.rotation;
  return next;
}

/** Mirrors an ENTIRE page (every panel + every bubble) horizontally. Pure —
 * returns a new `MangaPage`, never mutates the input. Applying it twice to
 * the same page restores the original geometry. */
export function mirrorPageHorizontally(page: MangaPage): MangaPage {
  const pageW = page.width || FALLBACK_PAGE_W;
  const pageH = page.height || FALLBACK_PAGE_H;
  const layout: MangaLayout = {
    panels: page.layout.panels.map(mirrorPanel),
    bubbles: page.layout.bubbles.map((b) => mirrorBubble(b, pageW, pageH)),
  };
  return { ...page, layout };
}
